import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { TORCH_GENESIS_KIND, TORCH_PASS_KIND } from '@/lib/torchConstants';
import { parseGenesisEvent, parsePassEvent, buildCanonicalChain } from '@/lib/torchEvents';
import type { GenesisEvent, ParsedChain } from '@/lib/torchTypes';

export interface MyTorch {
  genesis: GenesisEvent;
  parsed: ParsedChain;
}

export interface MyTorches {
  lit: MyTorch[];       // torches I created (genesis pubkey === me)
  holding: MyTorch[];   // torches currently in my hands (current holder === me, still alive)
}

/**
 * Fetch the user's torches:
 * - "Lit" — genesis events authored by the user
 * - "Holding" — torches where the user is the current holder and the torch is still lit
 */
export function useMyTorches(pubkey: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['my-torches', pubkey],
    queryFn: async ({ signal }) => {
      if (!pubkey) return { lit: [], holding: [] } as MyTorches;

      // 1. Torches I lit — genesis events I authored
      const myGenesisEvents = await nostr.query([{
        kinds: [TORCH_GENESIS_KIND],
        authors: [pubkey],
        limit: 100,
      }], { signal });

      const myGeneses: GenesisEvent[] = myGenesisEvents
        .map(parseGenesisEvent)
        .filter(Boolean) as GenesisEvent[];

      // 2. Pass events where I am the recipient — means I received the torch at some point
      const passedToMe = await nostr.query([{
        kinds: [TORCH_PASS_KIND],
        '#p': [pubkey],
        limit: 200,
      }], { signal });

      // Collect unique genesis refs from those pass events so we can load those torches
      const genesisRefsFromPasses = new Set<string>();
      for (const ev of passedToMe) {
        const parsed = parsePassEvent(ev);
        if (parsed) genesisRefsFromPasses.add(parsed.genesisRef);
      }

      // Also include genesis refs from torches I lit (in case I'm still the holder)
      for (const g of myGeneses) {
        genesisRefsFromPasses.add(`${TORCH_GENESIS_KIND}:${g.pubkey}:${g.slug}`);
      }

      // 3. For each unique genesis ref, load full chain and determine state + current holder
      const allTorches: MyTorch[] = [];

      await Promise.all(
        Array.from(genesisRefsFromPasses).map(async (ref) => {
          const parts = ref.split(':');
          if (parts.length !== 3) return;
          const [kindStr, creatorPubkey, slug] = parts;
          const kind = parseInt(kindStr, 10);

          const genesisEvents = await nostr.query([{
            kinds: [kind],
            authors: [creatorPubkey],
            '#d': [slug],
            limit: 1,
          }], { signal });

          const genesis = genesisEvents[0] ? parseGenesisEvent(genesisEvents[0]) : null;
          if (!genesis) return;

          const passEvents = await nostr.query([{
            kinds: [TORCH_PASS_KIND],
            '#a': [ref],
            limit: 500,
          }], { signal });

          const parsed = buildCanonicalChain({
            genesis,
            passes: passEvents.map(parsePassEvent).filter(Boolean) as NonNullable<ReturnType<typeof parsePassEvent>>[],
          });

          allTorches.push({ genesis, parsed });
        })
      );

      // Deduplicate by genesis id (in case a torch appears in both lit + holding lists)
      const seen = new Set<string>();
      const unique = allTorches.filter(t => {
        if (seen.has(t.genesis.id)) return false;
        seen.add(t.genesis.id);
        return true;
      });

      const lit: MyTorch[] = unique.filter(t => t.genesis.pubkey === pubkey);

      const holding: MyTorch[] = unique.filter(
        t => t.parsed.currentHolder === pubkey && t.parsed.state === 'lit'
      );

      return { lit, holding } as MyTorches;
    },
    enabled: !!pubkey,
  });
}
